import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ITodo } from '@clickersphere/core/entities/ITodo';
import { Button } from '../../Button';
import Hint from '../../Hint';
import DateInput from '../../DateInput';
import { Input } from '../../Input';
import Select from '../../Select';
import {
  type AssociationType,
  useRegisterTodoFormController,
} from './useRegisterTodoFormController';

interface ITodoFormProps {
  todo?: ITodo;
  leadId?: string;
  eventId?: string;
}

export default function TodoForm({ todo, leadId, eventId }: ITodoFormProps) {
  const { t } = useTranslation();
  const {
    isFetching,
    errors,
    handleSubmit,
    register,
    handleFormReset,
    associationType,
    control,
    leadOptions,
    eventOptions,
    handleAssociationTypeChange,
  } = useRegisterTodoFormController({
    todo,
    presetLeadId: leadId,
    presetEventId: eventId,
  });

  const associationOptions = [
    { value: 'none', text: t('todo.form.fields.associationNone') },
    { value: 'lead', text: t('todo.form.fields.associationLead') },
    { value: 'event', text: t('todo.form.fields.associationEvent') },
  ];

  return (
    <form
      className="my-4 flex w-full flex-col items-center gap-4"
      onSubmit={handleSubmit}
    >
      <Input
        type="text"
        placeholder={t('todo.form.fields.title')}
        {...register('title')}
        error={errors.title?.message}
        disabled={isFetching}
      />

      <Input
        type="text"
        placeholder={t('todo.form.fields.description')}
        {...register('description')}
        error={errors.description?.message}
        disabled={isFetching}
      />

      <div className="w-full space-y-1.5">
        <Controller
          control={control}
          name="dueDate"
          render={({
            field: { value, name, onChange },
            fieldState: { error },
          }) => (
            <DateInput
              name={name}
              value={value ?? ''}
              onChange={onChange}
              placeholder={t('todo.form.fields.dueDate')}
              error={error?.message}
              disabled={isFetching}
            />
          )}
        />
        <Hint>{t('todo.form.fields.dueDateHint')}</Hint>
      </div>

      <Controller
        name="associationType"
        control={control}
        render={({ field }) => (
          <Select
            placeholder={t('todo.form.fields.association')}
            options={associationOptions}
            value={field.value}
            onChange={(value) =>
              handleAssociationTypeChange(value as AssociationType)
            }
            disabled={isFetching}
            error={errors.associationType?.message}
          />
        )}
      />

      {associationType === 'lead' && (
        <Controller
          name="leadId"
          control={control}
          render={({ field }) => (
            <Select
              placeholder={t('todo.form.fields.lead')}
              options={leadOptions}
              searchable
              searchPlaceholder={t('todo.form.fields.leadSearchPlaceholder')}
              searchNoResultsText={t('todo.form.fields.leadNoResults')}
              value={field.value ?? ''}
              onChange={field.onChange}
              disabled={isFetching}
              error={errors.leadId?.message}
            />
          )}
        />
      )}

      {associationType === 'event' && (
        <Controller
          name="eventId"
          control={control}
          render={({ field }) => (
            <Select
              placeholder={t('todo.form.fields.event')}
              options={eventOptions}
              searchable
              searchPlaceholder={t('todo.form.fields.eventSearchPlaceholder')}
              searchNoResultsText={t('todo.form.fields.eventNoResults')}
              value={field.value ?? ''}
              onChange={field.onChange}
              disabled={isFetching}
              error={errors.eventId?.message}
            />
          )}
        />
      )}

      <section className="mt-4 flex w-full gap-4">
        <Button
          className="w-50"
          labelPath="register.clearFields"
          disabled={isFetching}
          color="ghost"
          onClick={() => handleFormReset()}
        />
        <Button
          labelPath="register.submit"
          type="submit"
          disabled={isFetching}
          isLoading={isFetching}
        />
      </section>
    </form>
  );
}
